<template>
  <ui-card name="audience.active_segments" rounded :error="error">
    <ui-dropdown
      slot="controls"
      class="level-right"
      :value="filter"
      :options="options"
      @change="onFilter"
    />

    <div class="list">
      <div class="list-header">
        <div class="list-header-item">
          <div class="header-name is-sort" @click="onSort('display_name')">
            {{ $t('audience.active_segments.list_title.type') }}
            <i
              class="material-icons icon-sort"
              v-html="getSort('display_name')"
            ></i>
          </div>
          <div class="header-amount is-sort" @click="onSort('amount')">
            <i class="material-icons icon-person">&#xE7FD;</i>
            {{ $t('audience.active_segments.list_title.amount') }}
            <i class="material-icons icon-sort" v-html="getSort('amount')"></i>
          </div>
        </div>
      </div>

      <div class="list-group-wrapper" ref="listGroupWrapper">
        <ul class="list-group">
          <li
            v-for="item in filteredSegments"
            :key="item.id"
            class="list-group-item"
          >
            <div class="name">{{ item.display_name }}</div>
            <div class="amount">
              <i class="material-icons icon-person">&#xE7FD;</i>
              {{ item.amount }}
            </div>
          </li>
        </ul>
      </div>
    </div>
  </ui-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import {
  map,
  filter,
  assignIn,
  orderBy,
  uniqBy,
  find,
  keyBy,
  values,
} from 'lodash';
import { humanize } from 'utils/string';
import Ui from 'services/Ui';
import { usersCf } from 'store/filters/audience';

const segmentsDim = usersCf.dimension(d => d._segments, true);

export default {
  name: 'ActiveSegments',

  data() {
    return {
      sortKey: 'amount',
      sortDir: 'desc',
      filter: 'all',
    };
  },

  computed: {
    ...mapState('audience', ['users']),

    ...mapGetters('account', {
      keyedSegmentDefinitions: 'segmentDefinitions',
    }),

    segmentDefinitions() {
      return values(this.keyedSegmentDefinitions);
    },

    segments() {
      const { users } = this; // dummy users access needed for crossfilter - don't delete!
      const group = keyBy(segmentsDim.group().all(), 'key');
      return map(this.segmentDefinitions, definition => {
        const id = group[definition.id];
        const amount = id ? id.value : 0;
        return assignIn({}, definition, { amount });
      });
    },

    sortedSegments() {
      return orderBy(this.segments, this.sortKey, this.sortDir);
    },

    filteredSegments() {
      return this.filter === 'all'
        ? filter(this.sortedSegments, segment => segment.amount > 0)
        : filter(
            this.sortedSegments,
            segment => segment.amount > 0 && segment.subcategory === this.filter
          );
    },

    options() {
      const segments = uniqBy(
        this.segmentDefinitions,
        definition => definition.subcategory
      );
      const options = segments.map(option => {
        return {
          value: option.subcategory,
          label: this.$t(`types.segments.subcategories.${option.subcategory}`),
        };
      });
      return Ui.makeOptions(options, {
        value: 'all',
        label: this.$t('audience.active_segments.dropdown.all'),
      });
    },

    error() {
      if (this.filteredSegments.length === 0) {
        return 'no_results';
      }

      return '';
    },
  },

  methods: {
    onFilter(filter) {
      this.filter = filter;
    },

    onSort(key) {
      if (key === this.sortKey) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      }
      this.sortKey = key;
    },

    getSort(key) {
      return key === this.sortKey
        ? this.sortDir === 'desc'
          ? '&#xE313;'
          : '&#xE316;'
        : '&#xE15B;';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.card {
  height: calc(100vh - 120px);
}

.card-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;
}

.list,
.list-group-wrapper {
  flex: 1;
  min-height: 0;
}

.list-group-wrapper {
  overflow-x: hidden;
  overflow-y: auto;
}

.list-header-item {
  &:last-child {
    > div {
      padding-right: 1.15rem;
    }
  }
}

.is-sort {
  cursor: pointer;
}

.header-name,
.name {
  flex: 1;
}

.header-amount,
.amount {
  min-width: 5.75rem;
}

.name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.amount {
  color: rgba($black, 0.64);
}

.icon-person {
  position: relative;
  top: 2px;
}

.icon-sort {
  position: relative;
  top: 3px;
  margin-right: 0;
  opacity: 0.5;
}
</style>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.active-segments-card {
  .card-content {
    min-height: 0;
  }

  .dropdown {
    &.is-small {
      .dropdown-content {
        &::after {
          content: ' ';
          position: absolute;
          display: block;
          bottom: 0;
          left: 0.5rem;
          width: calc(100% - 1rem);
          height: calc(100% - 0.5rem);
          box-shadow: 0 6px 24px 0 rgba($black, 0.16);
          z-index: -1;
        }
      }
    }
  }
}
</style>
